import { useState, useEffect } from "react";
import {
  useCreateArea,
  useOneArea,
  useUpdateArea,
} from "http-hooks/area/useArea";
import { Switch } from "@headlessui/react";
import Input from "components/ui/Input";
import Modal from "components/ui/modals";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function SetArea({ id, onClose, open }) {
  const [areaDetails, setAreaDetails] = useState({
    areaname: "",
    ag_status: 1,
  });
  const { isLoading: isLoadingArea, data } = useOneArea(id);

  const { mutateAsync: mutateCreate, isPending: isCreating } = useCreateArea();
  const { mutateAsync: mutateUpdate, isPending: isUpdating } = useUpdateArea();

  const isPending = isCreating || isUpdating;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAreaDetails((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleStatusChange = (e) => {
    setAreaDetails((prevData) => ({ ...prevData, ag_status: e ? 1 : 0 }));
  };

  useEffect(() => {
    if (data) {
      setAreaDetails((prev) => {
        const updatedDetails = {};
        Object.keys(prev).forEach((key) => {
          updatedDetails[key] = data[key] ?? prev[key];
        });
        return updatedDetails;
      });
    }
  }, [data]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (id) {
        await mutateUpdate({ id, data: areaDetails });
      } else {
        await mutateCreate(areaDetails);
      }
      onClose();
    } catch (error) {
      console.error("Operation failed:", error);
    }
  };

  return (
    <Modal
      open={open}
      setOpen={(val) => !val && onClose()}
      header={
        <h3 className="text-base font-semibold leading-6 text-gray-900">
          {id ? "Update" : "Add"} Area
        </h3>
      }
      footer={
        <div className="flex justify-end gap-x-3">
          <button
            type="button"
            className="text-sm font-semibold rounded-md px-3 py-2 hover:bg-gray-100 text-gray-900"
            onClick={onClose}
            disabled={isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="set-area-form"
            disabled={isPending}
            className="rounded-md bg-gray-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPending ? "Saving..." : "Save"}
          </button>
        </div>
      }
    >
      {id && isLoadingArea ? (
        <div className="animate-pulse space-y-6">
          <div className="h-9 bg-gray-200 rounded w-full" />
          <div className="h-6 bg-gray-200 rounded w-12" />
        </div>
      ) : (
        <form id="set-area-form" onSubmit={handleSubmit} className="space-y-6">
          <Input
            type="text"
            name="areaname"
            id="areaname"
            label="Area Name"
            maxLength="40"
            value={areaDetails.areaname}
            onChange={handleInputChange}
          />
          {id && (
            <div>
              <label className="block text-sm font-medium leading-6 text-gray-900">
                Status
              </label>
              <Switch
                checked={!!areaDetails.ag_status}
                onChange={handleStatusChange}
                className={classNames(
                  areaDetails.ag_status ? "bg-indigo-600" : "bg-gray-200",
                  "relative inline-flex ml-1 mt-3 h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2"
                )}
              >
                <span
                  aria-hidden="true"
                  className={classNames(
                    areaDetails.ag_status ? "translate-x-5" : "translate-x-0",
                    "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
                  )}
                />
              </Switch>
            </div>
          )}
        </form>
      )}
    </Modal>
  );
}
